import { useState, useCallback, useRef, useEffect } from 'react';
import { useRequest } from 'apis';
import { messageService } from 'services';
import useCallbackDebounce from 'components/hooks/useCallbackDebounce';
import { useTranslationX } from 'i18n';

export const useControleAlerta = (onDataRefresh?: () => void) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [periodosSelecionados, setPeriodosSelecionados] = useState<Set<string>>(
    new Set()
  );

  const periodosSelecionadosRef = useRef(periodosSelecionados);
  const { tx } = useTranslationX('dashboard', 'finance');

  const request = useRequest();

  useEffect(() => {
    periodosSelecionadosRef.current = periodosSelecionados;
  }, [periodosSelecionados]);

  const processarPeriodosAcumulados = useCallback(async () => {
    const periodosParaProcessar = Array.from(periodosSelecionadosRef.current);

    if (periodosParaProcessar.length === 0) {
      return;
    }

    setLoading(true);

    try {
      const alertaHashes = periodosParaProcessar;

      const resultado = await new Promise<{ success: boolean; data?: any; error?: any }>(
        resolve => {
          request({
            url: '/controle-alertas/ocultar',
            method: 'POST',
            data: {
              alertaHashes,
              tipo: 'PERIODO',
            },
            onSuccess: data => {
              resolve({ success: true, data });
            },
            onError: error => {
              resolve({ success: false, error });
            },
          });
        }
      );

      if (resultado.success) {
        const { sucessos = alertaHashes.length, falhas = 0 } = resultado.data || {};

        setPeriodosSelecionados(new Set());

        if (sucessos > 0) {
          const mensagem =
            sucessos === 1
              ? tx('nonCovered.successSingle')
              : tx('nonCovered.successMultiple', { count: sucessos });
          messageService.success(mensagem, { duration: 3000 });
        }

        if (falhas > 0) {
          const mensagem =
            falhas === 1
              ? tx('nonCovered.errorSingle')
              : tx('nonCovered.errorMultiple', { count: falhas });
          messageService.error(mensagem, { duration: 3000 });
        }

        if (sucessos > 0 && onDataRefresh) {
          onDataRefresh();
        }
      } else {
        messageService.error(tx('nonCovered.unexpectedError'), { duration: 3000 });
        setPeriodosSelecionados(new Set());
      }
    } catch (error) {
      messageService.error(tx('nonCovered.unexpectedError'), { duration: 3000 });
      setPeriodosSelecionados(new Set());
    }

    setLoading(false);
  }, [request, onDataRefresh, tx]);

  const processarPeriodosDebounced = useCallbackDebounce(
    processarPeriodosAcumulados,
    2000
  );

  const togglePeriodoSelecionado = useCallback(
    (periodoId: string): boolean => {
      setError(null);

      setPeriodosSelecionados(prev => {
        const newSet = new Set(prev);

        if (newSet.has(periodoId)) {
          newSet.delete(periodoId);
        } else {
          newSet.add(periodoId);
        }

        return newSet;
      });

      processarPeriodosDebounced();

      return true;
    },
    [processarPeriodosDebounced]
  );

  const isPeriodoSelecionado = useCallback(
    (periodoId: string) => {
      return periodosSelecionados.has(periodoId);
    },
    [periodosSelecionados]
  );

  return {
    togglePeriodoSelecionado,
    loading,
    error,
    isPeriodoSelecionado,
    periodosSelecionados,
  };
};
